import type { IdentifiedBasicContact } from "@repo/app-types/entities/contact";
import { buildInterviewContactDisplayNames } from "@repo/app-types/helpers/utilities";
import { getInitials } from "@repo/ui/helpers/helpers";
import { formatDistanceToNow } from "date-fns";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

interface ContactNameWithTimeProps {
  contact: IdentifiedBasicContact;
  date?: Date;
}

function getBackgroundColor(email: string) {
  const colors = [
    "bg-red-500",
    "bg-green-500",
    "bg-blue-500",
    "bg-indigo-500",
    "bg-orange-500",
    "bg-amber-500",
    "bg-yellow-500",
    "bg-lime-500",
    "bg-green-500",
    "bg-emerald-500",
    "bg-teal-500",
    "bg-cyan-500",
    "bg-sky-500",
    "bg-blue-500",
    "bg-indigo-500",
    "bg-rose-500",
    "bg-pink-500",
    "bg-fuchsia-500",
    "bg-purple-500",
    "bg-violet-500",
  ];

  // Use a hash function to consistently map the email to an index in the colors array
  const hashCode = email
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const colorIndex = hashCode % colors.length;

  return colors[colorIndex];
}

export const ContactNameWithTime: FC<ContactNameWithTimeProps> = ({
  contact,
  date,
}) => {
  const { primaryDisplayName, secondaryDisplayName, tertiaryDisplayName } =
    buildInterviewContactDisplayNames(contact);

  const initials = getInitials(
    contact.email,
    contact.first_name,
    contact.last_name,
  );

  return (
    <div className="flex items-center space-x-1">
      <div
        className={twMerge(
          "mt-0.5 mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-center text-white",
          getBackgroundColor(contact.id),
        )}
        style={{ fontSize: "0.7rem" }}
      >
        {initials.toUpperCase()}
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center">
          <span className="text-md truncate font-bold text-foreground">
            {primaryDisplayName}
          </span>
          {date && (
            <>
              <span className="mx-1">&middot;</span>
              <span className="whitespace-nowrap text-muted-foreground">
                {formatDistanceToNow(date, {
                  addSuffix: true,
                })}
              </span>
            </>
          )}
        </div>
        {secondaryDisplayName && (
          <div className={`text-sm text-gray-600`}>
            {`${secondaryDisplayName}${
              tertiaryDisplayName ? `, ${tertiaryDisplayName}` : ""
            }`}
          </div>
        )}
      </div>
    </div>
  );
};
